import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

function getSocketUrl() {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    try {
        const url = new URL(apiUrl);

        url.pathname = url.pathname.replace(/\/api\/?$/, '') || '/';

        url.search = '';
        url.hash = '';

        return url.toString().replace(/\/$/, '');
    } catch (error) {
        console.warn('Geçersiz REACT_APP_API_URL, varsayılan socket URL kullanılıyor:', apiUrl);
        return 'http://localhost:5000';
    }
}

const SOCKET_URL = getSocketUrl();

export function useSocket() {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
}

export function SocketProvider({ children }) {
    const { user, isAuthenticated } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const socketRef = useRef(null);
    const activeChannels = useRef(new Set());
    const listenersRef = useRef(new Map());

    // Single socket connection - runs once on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const activeChannelsRef = activeChannels.current;
        const listenersMap = listenersRef.current;

        console.log('🔌 Socket bağlantısı kuruluyor...', SOCKET_URL);

        // Single socket connection (no namespace)
        const socket = io(SOCKET_URL, {
            auth: token ? { token } : {},
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('🔌 Socket bağlandı, id:', socket.id);
            setIsConnected(true);

            // Rejoin active channels on reconnect
            activeChannelsRef.forEach(channel => {
                console.log('📺 Kanal yeniden bağlanıyor:', channel);
                socket.emit('channel:join', channel);
            });

            // Re-register listeners
            listenersMap.forEach((callbacks, event) => {
                callbacks.forEach(cb => {
                    socket.on(event, cb);
                });
            });
        });

        socket.on('disconnect', (reason) => {
            console.log('🔌 Socket bağlantısı kesildi:', reason);
            setIsConnected(false);
        });

        socket.on('connect_error', (err) => {
            console.error('Socket bağlantı hatası:', err.message);
            setIsConnected(false);
        });

        // Global notification handler
        socket.on('support:notification', (data) => {
            console.log('📬 Bildirim alındı:', data);
            setNotifications(prev => [...prev, { ...data, id: Date.now() }]);
        });

        return () => {
            console.log('🔌 Socket temizleniyor...');
            socket.disconnect();
            socketRef.current = null;
            activeChannelsRef.clear();
            listenersMap.clear();
        };
    }, []);

    // Update auth when user logs in/out
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket) return;

        const token = localStorage.getItem('token');

        if (isAuthenticated && user && token) {
            // Update auth and reconnect if needed
            if (socket.auth?.token !== token) {
                console.log('🔑 Auth token güncelleniyor...');
                socket.auth = { token };
                if (socket.connected) {
                    socket.disconnect().connect();
                }
            }
        } else if (!isAuthenticated) {
            // Clear auth on logout
            socket.auth = {};
        }
    }, [isAuthenticated, user]);

    // ═══════════════════════════════════════════
    // CHANNEL MANAGEMENT
    // ═══════════════════════════════════════════

    const joinChannel = useCallback((channel) => {
        const socket = socketRef.current;
        activeChannels.current.add(channel);

        if (socket?.connected) {
            console.log('📺 Kanala katılınıyor:', channel);
            socket.emit('channel:join', channel);
        } else {
            console.log('📺 Kanal kaydedildi (bağlantı bekliyor):', channel);
        }
    }, []);

    const leaveChannel = useCallback((channel) => {
        const socket = socketRef.current;
        activeChannels.current.delete(channel);

        if (socket?.connected) {
            console.log('📺 Kanaldan ayrılınıyor:', channel);
            socket.emit('channel:leave', channel);
        }
    }, []);

    // ═══════════════════════════════════════════
    // SUPPORT CHANNEL METHODS
    // ═══════════════════════════════════════════

    const joinTicket = useCallback((ticketId) => {
        const socket = socketRef.current;
        if (socket?.connected) {
            console.log('📩 Ticket odasına katılınıyor:', ticketId);
            socket.emit('support:join', ticketId);
        } else {
            console.log('⚠️ Socket bağlı değil - ticket katılımı bekliyor');
        }
    }, []);

    const leaveTicket = useCallback((ticketId) => {
        const socket = socketRef.current;
        if (socket) {
            socket.emit('support:leave', ticketId);
        }
    }, []);

    const sendSupportMessage = useCallback((ticketId, message) => {
        const socket = socketRef.current;
        if (socket?.connected) {
            console.log('💬 Mesaj gönderiliyor:', { ticketId, message });
            socket.emit('support:message', { ticketId, message });
            return true;
        }
        console.log('⚠️ Socket bağlı değil, mesaj gönderilemedi');
        return false;
    }, []);

    const sendTyping = useCallback((ticketId, isTyping) => {
        const socket = socketRef.current;
        if (socket?.connected) {
            socket.emit('support:typing', { ticketId, isTyping });
        }
    }, []);

    // ═══════════════════════════════════════════
    // MARKET CHANNEL METHODS
    // ═══════════════════════════════════════════

    const requestMarketData = useCallback(() => {
        const socket = socketRef.current;
        if (socket?.connected) {
            socket.emit('market:request');
        }
    }, []);

    // ═══════════════════════════════════════════
    // EVENT SUBSCRIPTION
    // ═══════════════════════════════════════════

    const on = useCallback((event, callback) => {
        // Track listener for reconnection
        if (!listenersRef.current.has(event)) {
            listenersRef.current.set(event, new Set());
        }
        listenersRef.current.get(event).add(callback);

        // Register immediately if connected
        const socket = socketRef.current;
        if (socket) {
            socket.on(event, callback);
            console.log('📡 Event dinleniyor:', event);
        }
    }, []);

    const off = useCallback((event, callback) => {
        // Remove from tracked listeners
        if (listenersRef.current.has(event)) {
            listenersRef.current.get(event).delete(callback);
            if (listenersRef.current.get(event).size === 0) {
                listenersRef.current.delete(event);
            }
        }

        // Unregister from socket
        const socket = socketRef.current;
        if (socket) {
            socket.off(event, callback);
            console.log('📡 Event dinleme bırakıldı:', event);
        }
    }, []);

    // ═══════════════════════════════════════════
    // NOTIFICATIONS
    // ═══════════════════════════════════════════

    const clearNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const value = {
        // Connection
        isConnected,
        socket: socketRef.current,

        // Channels
        joinChannel,
        leaveChannel,

        // Support
        joinTicket,
        leaveTicket,
        sendSupportMessage,
        sendTyping,

        // Market
        requestMarketData,

        // Events
        on,
        off,

        // Notifications
        notifications,
        clearNotification,
        clearAllNotifications,
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
}
